import React, { useState, useEffect } from "react";
import {
  Modal,
  Button,
  Form,
  Spinner,
  Alert,
  Row,
  Col,
  Card
} from "react-bootstrap";
import axios from "axios";

/* ───────────────────────── helpers ───────────────────────── */

// ◆ ADDED – one place to build auth header
const authHeader = () => ({
  Authorization: `Bearer ${localStorage.getItem("access_token")}`
});

/* ───────────────────────── component ───────────────────────── */

function SeasonScheduleWizard({
  show,
  handleClose,
  onScheduleGenerated,
  seasonId,
  scheduleLocked
}) {
  /* ---------- state (unchanged) ---------- */
  const [formData, setFormData] = useState({
    scheduleType: "weekly",
    startDate: "",
    endDate: "",
    preseasonGames: 0,
    regularSeasonGames: 1,
    makeupGames: 0,
    playoffGames: 0,
    allowDoubleHeaders: false,
    maxConsecutiveGames: 1,
    fieldSlots: [],
    gameDuration: 60,
    timeSlots: []
  });
  const [teams, setTeams] = useState([]);
  const [locations, setLocations] = useState([]);
  const [previewSchedule, setPreviewSchedule] = useState([]);
  const [scheduleSummary, setScheduleSummary] = useState(null);
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [refreshing, setRefreshing] = useState(false);
  const [refreshSuccess, setRefreshSuccess] = useState(false);
  const [scheduleWarning, setScheduleWarning] = useState(null); // still used
  const [previewRequestAbortController, setPreviewRequestAbortController] = useState(null);
  const [hasPreviewed, setHasPreviewed] = useState(false); // Track if preview has been generated
  const [currentSeed, setCurrentSeed] = useState(null);

  /* ---------- fetch teams / locations / season dates when modal opens ---------- */
  useEffect(() => {
    if (!show) return;

    // Reset preview state when modal opens
    setHasPreviewed(false);
    setPreviewSchedule([]);
    setScheduleSummary(null);
    setCurrentSeed(null); // Reset seed for fresh random order

    axios
      .get(`/api/teams/${seasonId}/teams/`, { headers: authHeader() })
      .then((r) => setTeams(r.data))
      .catch((e) => console.error(e));

    axios
      .get("/api/leagues/locations/", { headers: authHeader() })
      .then((r) => setLocations(r.data))
      .catch((e) => console.error(e));

    axios
      .get(`/api/leagues/seasons/${seasonId}/`, { headers: authHeader() })
      .then((r) => {
        const toInput = (d) =>
          d ? new Date(d).toISOString().split("T")[0] : "";
        setFormData((p) => ({
          ...p,
          startDate: toInput(r.data.start_date),
          endDate: toInput(r.data.end_date)
        }));
      })
      .catch((e) => console.error(e));
  }, [show, seasonId]);

  /* ---------- cleanup on unmount ---------- */
  useEffect(() => {
    return () => {
      // Cancel any pending requests when component unmounts
      if (previewRequestAbortController) {
        previewRequestAbortController.abort();
      }
    };
  }, [previewRequestAbortController]);

  /* ---------- handlers ---------- */
  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((p) => ({ ...p, [name]: type === "checkbox" ? checked : value }));
  };

  const handleFieldSlotChange = (id, checked) => {
    setFormData((p) => ({
      ...p,
      fieldSlots: checked ? [...p.fieldSlots, id] : p.fieldSlots.filter((x) => x !== id)
    }));
  };

  const addTimeSlot = () =>
    setFormData((p) => ({
      ...p,
      timeSlots: [...p.timeSlots, { day: "monday", time: "18:00", fieldId: "" }]
    }));

  const removeTimeSlot = (idx) =>
    setFormData((p) => ({
      ...p,
      timeSlots: p.timeSlots.filter((_, i) => i !== idx)
    }));

  const updateTimeSlot = (idx, field, value) =>
    setFormData((p) => ({
      ...p,
      timeSlots: p.timeSlots.map((s, i) =>
        i === idx ? { ...s, [field]: value } : s
      )
    }));

  /* ---------- PREVIEW: ask backend ---------- */
  const fetchPreview = async (overrideSeed = null) => {
    // Cancel any existing request
    if (previewRequestAbortController) {
      previewRequestAbortController.abort();
    }

    // Create new abort controller for this request
    const abortController = new AbortController();
    setPreviewRequestAbortController(abortController);

    try {
      const seedToSend = overrideSeed !== undefined ? overrideSeed : currentSeed;
      const { data } = await axios.post(
        `/api/games/preview/schedule/${seasonId}/`,
        {
          ...formData,
          shuffleSeed: seedToSend // Send current seed (null for first time, actual seed for refresh)
        },
        {
          headers: authHeader(),
          signal: abortController.signal,
          timeout: 30000 // 30 second timeout
        }
      );
      /* backend returns fixtures:
         {
            date: "2025-07-08",
            time: "17:45",
            location: "Field A",
            home_team: {id,name,logo},
            away_team: {id,name,logo}
         }
      */
      setPreviewSchedule(data.fixtures); // ◆ UPDATED
      setCurrentSeed(data.seed); // Store the seed returned from backend
      buildSummary(data.fixtures); // ◆ ADDED
      setScheduleWarning(null);
      setHasPreviewed(true);
    } catch (err) {
      // Don't show error if request was cancelled
      if (err.name === 'AbortError' || err.code === 'ERR_CANCELED') {
        return;
      }
      setErrorMessage(err.response?.data?.error || "Preview failed");
    } finally {
      setPreviewRequestAbortController(null);
    }
  };

  /* ---------- summary builder ---------- */
  const buildSummary = (fixtures) => {
    const counts = Object.fromEntries(teams.map((t) => [t.id, 0]));
    const regularGameCounts = Object.fromEntries(teams.map((t) => [t.id, 0]));
    let actualGames = 0;
    let byeGames = 0;

    fixtures.forEach((g) => {
      // Handle both new backend structure (home_team.id) and old structure (homeTeamId)
      const homeTeamId = g.home_team?.id || g.homeTeamId;
      const awayTeamId = g.away_team?.id || g.awayTeamId;
      const gameType = g.game_type || 'regular';

      if (gameType === 'bye') {
        // Bye games count for the home team in total counts
        if (homeTeamId && homeTeamId !== "placeholder1" && homeTeamId !== "bye") {
          counts[homeTeamId] += 1;
          byeGames += 1;
        }
      } else {
        // Regular games count for both teams
        if (homeTeamId && homeTeamId !== "placeholder1") {
          counts[homeTeamId] += 1;
          regularGameCounts[homeTeamId] += 1;
          actualGames += 1;
        }
        if (awayTeamId && awayTeamId !== "placeholder1") {
          counts[awayTeamId] += 1;
          regularGameCounts[awayTeamId] += 1;
          actualGames += 1;
        }
      }
    });

    setScheduleSummary({
      totalGames: fixtures.length,
      actualGames: actualGames / 2, // Divide by 2 since each game involves 2 teams
      byeGames: byeGames,
      averageGamesPerTeam: (Object.values(counts).reduce((sum, count) => sum + count, 0) / teams.length).toFixed(1),
      teamGameCounts: Object.entries(regularGameCounts).map(([id, ct]) => ({
        teamName: teams.find((t) => t.id === parseInt(id))?.name || `Team ${id}`,
        gameCount: ct
      }))
    });
  };

  /* ---------- auto-preview on relevant changes ---------- */
  // Removed automatic preview - now only manual via button

  /* ---------- refresh button ---------- */
  const handleRefresh = async () => {
    setRefreshing(true);
    // Pass null explicitly to force generation of new random seed
    await fetchPreview(null);
    setRefreshing(false);
    setRefreshSuccess(true);
    setTimeout(() => setRefreshSuccess(false), 2500);
  };

  /* ---------- GENERATE schedule ---------- */
  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await axios.post(
        `/api/games/generate/schedule/${seasonId}/`,
        { ...formData, shuffleSeed: currentSeed },
        { headers: authHeader() }
      );
      onScheduleGenerated(data.games);
      handleClose();
    } catch (err) {
      setErrorMessage(err.response?.data?.error || "Schedule generation failed");
    } finally {
      setLoading(false);
    }
  };

  /* ---------- helper arrays ---------- */
  const daysOfWeek = [
    { value: "monday", label: "Monday" },
    { value: "tuesday", label: "Tuesday" },
    { value: "wednesday", label: "Wednesday" },
    { value: "thursday", label: "Thursday" },
    { value: "friday", label: "Friday" },
    { value: "saturday", label: "Saturday" },
    { value: "sunday", label: "Sunday" }
  ];

  return (
    <Modal show={show} onHide={handleClose} size="xl" centered>
      <Modal.Header closeButton>
        <Modal.Title>
          <i className="bi bi-magic me-2"></i>
          Season Schedule Wizard
        </Modal.Title>
      </Modal.Header>
      <Modal.Body className="p-0">
        {scheduleLocked && (
          <div className="position-fixed top-0 start-50 translate-middle-x mt-2" style={{ zIndex: 1060 }}>
            <Alert variant="warning" className="mb-0 shadow-sm" style={{ maxWidth: '400px' }}>
              <div className="d-flex align-items-center">
                <i className="bi bi-exclamation-triangle me-2"></i>
                <div>
                  <strong>Schedule is Locked!</strong>
                  <div className="small">The schedule has already been generated and is locked.</div>
                </div>
                <Button variant="outline-secondary" size="sm" className="ms-2" onClick={handleClose}>
                  Close
                </Button>
              </div>
            </Alert>
          </div>
        )}
        {!scheduleLocked && (
          <>
            {errorMessage && (
              <div className="position-fixed top-0 start-50 translate-middle-x mt-2" style={{ zIndex: 1060 }}>
                <Alert variant="danger" className="mb-0 shadow-sm" style={{ maxWidth: '400px' }}>
                  <div className="d-flex align-items-center">
                    <i className="bi bi-exclamation-circle me-2"></i>
                    <div className="flex-grow-1">{errorMessage}</div>
                    <Button variant="outline-danger" size="sm" onClick={() => setErrorMessage("")}>
                      <i className="bi bi-x"></i>
                    </Button>
                  </div>
                </Alert>
              </div>
            )}
            {refreshSuccess && (
              <div className="position-fixed top-0 start-50 translate-middle-x mt-2" style={{ zIndex: 1060 }}>
                <Alert variant="success" className="mb-0 shadow-sm" style={{ maxWidth: '400px' }}>
                  <div className="d-flex align-items-center">
                    <i className="bi bi-check-circle me-2"></i>
                    <div className="flex-grow-1">Schedule preview updated successfully!</div>
                    <Button variant="outline-success" size="sm" onClick={() => setRefreshSuccess(false)}>
                      <i className="bi bi-x"></i>
                    </Button>
                  </div>
                </Alert>
              </div>
            )}

            <div className="row g-0 h-100">
              {/* Configuration Pane - 2/3 width */}
              <div className="col-8 p-3" style={{ maxHeight: '70vh', overflowY: 'auto' }}>
                <Form onSubmit={handleSubmit}>
                  {/* Basic Schedule Configuration */}
                  <Card className="mb-4">
                    <Card.Header>
                      <h6 className="mb-0">Schedule Configuration</h6>
                    </Card.Header>
                    <Card.Body>
                      <Row>
                        <Col md={6}>
                          <Form.Group className="mb-3">
                            <Form.Label>Schedule Type</Form.Label>
                            <Form.Select
                              name="scheduleType"
                              value={formData.scheduleType}
                              onChange={handleChange}
                            >
                              <option value="weekly">Weekly</option>
                              <option value="monthly">Monthly</option>
                              <option value="multiple_times_week">Multiple Times per Week</option>
                            </Form.Select>
                          </Form.Group>
                        </Col>
                        <Col md={6}>
                          <Form.Group className="mb-3">
                            <Form.Label>Game Duration (minutes)</Form.Label>
                            <Form.Control
                              type="number"
                              name="gameDuration"
                              value={formData.gameDuration}
                              onChange={handleChange}
                              min="30"
                              max="180"
                            />
                          </Form.Group>
                        </Col>
                      </Row>

                      <Row>
                        <Col md={6}>
                          <Form.Group className="mb-3">
                            <Form.Label>Start Date</Form.Label>
                            <Form.Control
                              type="date"
                              name="startDate"
                              value={formData.startDate}
                              onChange={handleChange}
                              required
                            />
                          </Form.Group>
                        </Col>
                        <Col md={6}>
                          <Form.Group className="mb-3">
                            <Form.Label>End Date</Form.Label>
                            <Form.Control
                              type="date"
                              name="endDate"
                              value={formData.endDate}
                              onChange={handleChange}
                              required
                            />
                          </Form.Group>
                        </Col>
                      </Row>
                    </Card.Body>
                  </Card>

                  {/* Game Counts */}
                  <Card className="mb-4">
                    <Card.Header>
                      <h6 className="mb-0">Game Counts</h6> <span className="text-muted">* per team</span>
                    </Card.Header>
                    <Card.Body>
                      <Row>
                        <Col md={3}>
                          <Form.Group className="mb-3">
                            <Form.Label>Preseason</Form.Label>
                            <Form.Control
                              type="number"
                              name="preseasonGames"
                              value={formData.preseasonGames}
                              onChange={handleChange}
                              min="0"
                              max="10"
                            />
                          </Form.Group>
                        </Col>
                        <Col md={3}>
                          <Form.Group className="mb-3">
                            <Form.Label>Regular Season</Form.Label>
                            <Form.Control
                              type="number"
                              name="regularSeasonGames"
                              value={formData.regularSeasonGames}
                              onChange={handleChange}
                              min="1"
                              max="20"
                            />
                          </Form.Group>
                        </Col>
                        <Col md={3}>
                          <Form.Group className="mb-3">
                            <Form.Label>Makeup</Form.Label>
                            <Form.Control
                              type="number"
                              name="makeupGames"
                              value={formData.makeupGames}
                              onChange={handleChange}
                              min="0"
                              max="10"
                            />
                          </Form.Group>
                        </Col>
                        <Col md={3}>
                          <Form.Group className="mb-3">
                            <Form.Label>Playoff</Form.Label>
                            <Form.Control
                              type="number"
                              name="playoffGames"
                              value={formData.playoffGames}
                              onChange={handleChange}
                              min="0"
                              max="10"
                            />
                          </Form.Group>
                        </Col>
                      </Row>
                    </Card.Body>
                  </Card>

                  {/* Teams Display */}
                  <Card className="mb-4">
                    <Card.Header>
                      <h6 className="mb-0">Teams in Season ({teams.length})</h6>
                    </Card.Header>
                    <Card.Body>
                      <Row>
                        {teams.map((team) => (
                          <Col key={team.id} md={6} className="mb-2">
                            <div className="d-flex align-items-center">
                              <img
                                src={team.logo || `/static/images/kb_static/lemont_rec_leagues_circle.png`}
                                alt={`${team.name} logo`}
                                style={{ width: "30px", height: "30px", marginRight: "10px" }}
                              />
                              <span>{team.name}</span>
                            </div>
                          </Col>
                        ))}
                      </Row>
                    </Card.Body>
                  </Card>

                  {/* Field/Location Selection */}
                  <Card className="mb-4">
                    <Card.Header>
                      <h6 className="mb-0">Available Fields/Locations</h6>
                    </Card.Header>
                    <Card.Body>
                      <Row>
                        {locations.map((location) => (
                          <Col key={location.id} md={6} className="mb-2">
                            <Form.Check
                              type="checkbox"
                              id={`location-${location.id}`}
                              label={`${location.name} - ${location.city}`}
                              checked={formData.fieldSlots.includes(location.id)}
                              onChange={(e) => handleFieldSlotChange(location.id, e.target.checked)}
                            />
                          </Col>
                        ))}
                      </Row>
                    </Card.Body>
                  </Card>

                  {/* Time Slots */}
                  <Card className="mb-4">
                    <Card.Header>
                      <div className="d-flex justify-content-between align-items-center">
                        <h6 className="mb-0">Time Slots</h6>
                        <Button variant="outline-primary" size="sm" onClick={addTimeSlot}>
                          <i className="bi bi-plus me-1"></i>Add Time Slot
                        </Button>
                      </div>
                    </Card.Header>
                    <Card.Body>
                      {formData.timeSlots.map((slot, index) => (
                        <Row key={index} className="mb-2">
                          <Col md={3}>
                            <Form.Select
                              value={slot.day}
                              onChange={(e) => updateTimeSlot(index, 'day', e.target.value)}
                            >
                              {daysOfWeek.map(day => (
                                <option key={day.value} value={day.value}>
                                  {day.label}
                                </option>
                              ))}
                            </Form.Select>
                          </Col>
                          <Col md={3}>
                            <Form.Control
                              type="time"
                              value={slot.time}
                              onChange={(e) => updateTimeSlot(index, 'time', e.target.value)}
                            />
                          </Col>
                          <Col md={4}>
                            <Form.Select
                              value={slot.fieldId}
                              onChange={(e) => updateTimeSlot(index, 'fieldId', e.target.value)}
                            >
                              <option value="">Any Field</option>
                              {locations.map(location => (
                                <option key={location.id} value={location.id}>
                                  {location.name}
                                </option>
                              ))}
                            </Form.Select>
                          </Col>
                          <Col md={2}>
                            <Button
                              variant="outline-danger"
                              size="sm"
                              onClick={() => removeTimeSlot(index)}
                            >
                              <i className="bi bi-trash"></i>
                            </Button>
                          </Col>
                        </Row>
                      ))}
                      {formData.timeSlots.length === 0 && (
                        <p className="text-muted">No time slots added. Click "Add Time Slot" to get started.</p>
                      )}
                    </Card.Body>
                  </Card>

                  {/* Advanced Options */}
                  <Card className="mb-4">
                    <Card.Header>
                      <h6 className="mb-0">Advanced Options</h6>
                    </Card.Header>
                    <Card.Body>
                      <Row>
                        <Col md={6}>
                          <Form.Check
                            type="checkbox"
                            id="allowDoubleHeaders"
                            label="Allow Double Headers"
                            name="allowDoubleHeaders"
                            checked={formData.allowDoubleHeaders}
                            onChange={handleChange}
                          />
                          <small className="text-muted d-block">
                            Allow teams to play multiple games in the same day
                          </small>
                        </Col>
                        <Col md={6}>
                          <Form.Group>
                            <Form.Label>Max Consecutive Games</Form.Label>
                            <Form.Control
                              type="number"
                              name="maxConsecutiveGames"
                              value={formData.maxConsecutiveGames}
                              onChange={handleChange}
                              min="1"
                              max="5"
                            />
                            <small className="text-muted">
                              Maximum number of consecutive times a team can play the same opponent
                            </small>
                          </Form.Group>
                        </Col>
                      </Row>
                    </Card.Body>
                  </Card>
                </Form>
              </div>

              {/* Preview Pane - 1/3 width */}
              <div className="col-4 border-start p-3" style={{ maxHeight: '70vh', overflowY: 'auto' }}>
                <div className="d-flex justify-content-between align-items-center mb-3">
                  <h6 className="mb-0">Schedule Preview</h6>
                  <Button
                    variant="outline-info"
                    size="sm"
                    onClick={handleRefresh}
                    disabled={
                      teams.length < 2 ||
                      formData.fieldSlots.length === 0 ||
                      formData.timeSlots.length === 0 ||
                      !formData.startDate ||
                      !formData.endDate ||
                      refreshing
                    }
                  >
                    {refreshing ? (
                      <>
                        <Spinner animation="border" size="sm" className="me-1" />
                        {hasPreviewed ? "Generating New Order..." : "Generating Preview..."}
                      </>
                    ) : (
                      <>
                        <i className={`bi ${hasPreviewed ? 'bi-shuffle' : 'bi-eye'} me-1`}></i>
                        {hasPreviewed ? "New Random Order" : "Preview"}
                      </>
                    )}
                  </Button>
                </div>

                {scheduleSummary && (
                  <div className="card mb-3">
                    <div className="card-header">
                      <h6 className="mb-0">Schedule Summary</h6>
                    </div>
                    <div className="card-body p-2">
                      <div className="row text-center mb-2">
                        <div className="col-6">
                          <div className="fw-bold text-primary">{scheduleSummary.actualGames}</div>
                          <small className="text-muted">Actual Games</small>
                        </div>
                        <div className="col-6">
                          <div className="fw-bold text-secondary">{scheduleSummary.byeGames}</div>
                          <small className="text-muted">Bye Games</small>
                        </div>
                      </div>
                      <div className="row text-center mb-2">
                        <div className="col-6">
                          <div className="fw-bold text-info">{scheduleSummary.totalGames}</div>
                          <small className="text-muted">Total Slots</small>
                        </div>
                        <div className="col-6">
                          <div className="fw-bold text-success">{scheduleSummary.averageGamesPerTeam}</div>
                          <small className="text-muted">Avg Games/Team</small>
                        </div>
                      </div>
                      <hr className="my-2" />
                      <div className="team-breakdown">
                        <small className="text-muted d-block mb-1">Games per Team:</small>
                        {scheduleSummary.teamGameCounts.map((team, index) => (
                          <div key={index} className="d-flex justify-content-between align-items-center mb-1">
                            <small className="text-truncate me-2" style={{ maxWidth: '60%' }}>
                              {team.teamName}
                            </small>
                            <small className="fw-bold">{team.gameCount}</small>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                )}

                {scheduleWarning && (
                  <div className={`alert alert-${scheduleWarning.type === 'warning' ? 'warning' : 'info'} alert-sm mb-3`}>
                    <small>
                      <i className={`bi ${scheduleWarning.type === 'warning' ? 'bi-exclamation-triangle' : 'bi-info-circle'} me-1`}></i>
                      {scheduleWarning.message}
                    </small>
                  </div>
                )}

                {previewSchedule.length > 0 ? (
                  <div className="schedule-preview">
                    {previewSchedule.some(game => game.home_team?.id === "placeholder1" || game.away_team?.id === "placeholder1") && (
                      <div className="alert alert-info alert-sm mb-3">
                        <small>
                          <i className="bi bi-info-circle me-1"></i>
                          Makeup and playoff games show placeholder teams. Actual teams will be assigned when games are scheduled.
                        </small>
                      </div>
                    )}
                    {previewSchedule.some(game => game.game_type === 'bye') && (
                      <div className="alert alert-secondary alert-sm mb-3">
                        <small>
                          <i className="bi bi-calendar-x me-1"></i>
                          Bye games are shown for teams that don't have a regular game scheduled that week.
                        </small>
                      </div>
                    )}
                    {previewSchedule.map((game, index) => (
                      <div key={`${game.date}-${game.time}-${game.location_id}-${index}`} className="card mb-2">
                        <div className="card-body p-2">
                          <div className="d-flex justify-content-between align-items-center mb-1">
                            <small className="text-muted">Game {index + 1}</small>
                            <div className="d-flex align-items-center gap-1">
                              <small className="text-muted">{game.date}</small>
                              <span className={`badge badge-sm ${game.game_type === 'preseason' ? 'bg-info' :
                                game.game_type === 'regular' ? 'bg-primary' :
                                  game.game_type === 'makeup' ? 'bg-warning' :
                                    game.game_type === 'playoff' ? 'bg-success' :
                                      game.game_type === 'bye' ? 'bg-secondary' :
                                        'bg-primary'
                                }`}>
                                {game.game_type || 'regular'}
                              </span>
                            </div>
                          </div>
                          <div className="fw-bold small">{game.home_team?.name || game.homeTeam || 'TBD'}</div>
                          <div className="text-center small text-muted">
                            {game.game_type === 'bye' ? 'BYE' : 'vs'}
                          </div>
                          <div className="fw-bold small">{game.away_team?.name || game.awayTeam || 'TBD'}</div>
                          <div className="mt-1">
                            <small className="text-muted">
                              <i className="bi bi-geo-alt me-1"></i>
                              {game.location}
                            </small>
                          </div>
                          <div>
                            <small className="text-muted">
                              <i className="bi bi-clock me-1"></i>
                              {game.time}
                            </small>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center text-muted py-4">
                    <i className="bi bi-calendar-event fs-1 mb-3"></i>
                    <p>Configure your schedule settings and click "Preview" to see a preview</p>
                  </div>
                )}
              </div>
            </div>
          </>
        )}
      </Modal.Body>
      {!scheduleLocked && (
        <Modal.Footer>
          <Button variant="secondary" onClick={handleClose}>
            Cancel
          </Button>
          <Button
            variant="primary"
            type="submit"
            disabled={loading || teams.length < 2}
            onClick={handleSubmit}
          >
            {loading ? (
              <>
                <Spinner animation="border" size="sm" className="me-2" />
                Generating Schedule...
              </>
            ) : (
              <>
                <i className="bi bi-magic me-1"></i>
                Generate Schedule
              </>
            )}
          </Button>
        </Modal.Footer>
      )}
    </Modal>
  );
}

export default SeasonScheduleWizard;